/**
 * AI Chat — isolated config (env only).
 *
 * REACT_APP_AI_CHAT_ENABLED — optional kill switch: set to 'false' to hide the
 *   chat UI app-wide. When unset or any other value, chat is allowed subject to
 *   path rules and per-module enableChat (Project Settings → Chat AI).
 *
 * REACT_APP_AI_CHAT_PATH_PATTERNS — optional override:
 *   - empty / unset: default rules (project sidebar pages; see below).
 *   - '*': any route except exclusions (settings, TSA Accelerator module).
 *   - comma-separated substrings or /regex/ for legacy path matching (still applies exclusions).
 *
 * Default visibility: chat on project workspace routes opened from the sidebar
 * (Project Home, Questionnaire, Side x Side /analysis, Insights / IMO Launch area,
 * Synergy Tracker, Workforce Architect, Status Reporting, custom modules / link /
 * powerBI / etc.). Per-module gating (Project Settings → Chat AI) decides whether
 * Status Reporting actually shows the assistant, just like any other sidebar module.
 * Hidden on: Project Settings (/projects/setting), System Settings (/settings),
 * project list/create, and when moduleValue is tsaAccelerator.
 *
 * Per-module on/off: Project Settings → Chat AI tab (saved via project settings API).
 *
 * REACT_APP_AI_CHAT_BACKEND_MODE — 'dummy' | 'rest' (affects naming only for
 *   chat completions; there is no REST “AI chat settings” GET on DDV).
 * REACT_APP_AI_CHAT_API_BASE — optional base for paths in aiChat/api/paths.js
 *   (e.g. /chatbot/agents). Not used for core DDV GET/POST /project/settings/:id.
 * REACT_APP_AI_CHAT_USE_REST_MESSAGES — set to 'true' to POST to
 *   /projects/:id/ai-chat/messages; default off (dummy assistant replies) because
 *   that route is not deployed on standard DDV API.
 *
 * REACT_APP_AI_CHAT_USE_CHATBOT_SSE — POST /chatbot (SSE) for chat when
 *   REACT_APP_AI_CHAT_API_BASE is set. Default: SSE is on whenever the API base is set.
 *   Set to 'false' for non-SSE dummy replies or REACT_APP_AI_CHAT_USE_REST_MESSAGES instead.
 *
 * REACT_APP_AI_CHAT_WS_BASE — full WebSocket URL for chatbot (e.g. wss://host/ws/chatbot).
 *   No path joining: use the exact URL the gateway expects. Auth uses GET /chatbot/chat-id
 *   (Authorization header) then connects with ?chatId= on the handshake URL. The ticket is
 *   cached in localStorage (ddv:aiChat:chatbotChatId) for the browser session and cleared on logout.
 * REACT_APP_AI_CHAT_USE_CHATBOT_WS — set to 'true' to stream live chatbot turns over WebSocket
 *   when REACT_APP_AI_CHAT_WS_BASE is set. Precedence: when WebSocket mode is enabled, it is
 *   used for live chatbot turns even if SSE would otherwise be active (same API base). SSE
 *   remains for environments that only configure REACT_APP_AI_CHAT_API_BASE.
 *
 * REACT_APP_AI_CHAT_DEMO_RESPONSES — set to 'true' to bypass chatbot / REST conversation
 *   calls and stream a local Markdown demo reply (for UI demos when APIs are down).
 *
 * REACT_APP_AI_CHAT_BETA_CHIP — set to 'false' to hide the BETA chip in the
 *   pane header. Default is visible when unset.
 */

/** Sidebar module identifiers that must not show AI chat (TSA Accelerator). */
const EXCLUDED_MODULE_VALUES = new Set(['tsaAccelerator']);

function parseSearch(search) {
  if (!search || typeof search !== 'string') {
    return new URLSearchParams();
  }
  const q = search.startsWith('?') ? search.slice(1) : search;
  return new URLSearchParams(q);
}

/**
 * Routes where chat must not appear (pathname is router path, basename stripped).
 */
export function isAiChatPathExcluded(pathname, search = '') {
  if (!pathname) {
    return true;
  }
  const p = pathname.toLowerCase();

  if (p.includes('/projects/setting')) {
    return true;
  }
  if (p.includes('/projects/list') || p.includes('/projects/create')) {
    return true;
  }
  if (p.includes('email-link-redirect')) {
    return true;
  }
  if (p.startsWith('/settings') || p.includes('/settings/')) {
    return true;
  }

  const mv = parseSearch(search).get('moduleValue');
  if (mv && EXCLUDED_MODULE_VALUES.has(mv)) {
    return true;
  }

  return false;
}

/**
 * Default: show on typical project sidebar destinations (not list/create/settings).
 */
function pathnameMatchesDefaultAllowlist(pathname) {
  const markers = [
    'project-detail',
    'questionnaires',
    'analysis',
    'insights',
    'powerBI',
    'textContainer',
    'widgetLink',
    'multiSectionLink',
    'imageLink',
    'pdfLink',
    'htmlLink',
    'manageTiles',
  ];
  if (markers.some((m) => pathname.includes(m))) {
    return true;
  }
  // Custom iframe module: /link — avoid matching email-link-redirect
  if (/(?:^|\/)link\/?$/.test(pathname)) {
    return true;
  }
  return false;
}

function defaultProjectSidebarChatAllowed(pathname, search) {
  if (isAiChatPathExcluded(pathname, search)) {
    return false;
  }
  return pathnameMatchesDefaultAllowlist(pathname);
}

export function isAiChatGloballyEnabled() {
  return process.env.REACT_APP_AI_CHAT_ENABLED !== 'false';
}

/**
 * @param {string} pathname
 * @param {string} [search] — location.search (for moduleValue exclusions)
 */
export function isAiChatEnabledForPath(pathname, search = '') {
  if (!isAiChatGloballyEnabled() || !pathname) {
    return false;
  }

  const raw = process.env.REACT_APP_AI_CHAT_PATH_PATTERNS;
  if (raw == null || raw === '') {
    return defaultProjectSidebarChatAllowed(pathname, search);
  }
  if (raw.trim() === '*') {
    return !isAiChatPathExcluded(pathname, search);
  }

  const patterns = raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const legacyMatch = patterns.some((pat) => {
    if (pat.startsWith('/') && pat.endsWith('/')) {
      try {
        return new RegExp(pat.slice(1, -1)).test(pathname);
      } catch {
        return false;
      }
    }
    return pathname.includes(pat);
  });
  return legacyMatch && !isAiChatPathExcluded(pathname, search);
}

/** Base URL for REST calls; empty means no GET/POST to backend. */
export function getAiChatApiBase() {
  const u = process.env.REACT_APP_AI_CHAT_API_BASE;
  return u && u.trim() !== '' ? u.replace(/\/$/, '') : '';
}

/** 'dummy' | 'rest' */
export function getAiChatBackendMode() {
  const explicit = process.env.REACT_APP_AI_CHAT_BACKEND_MODE;
  if (explicit === 'dummy' || explicit === 'rest') {
    return explicit;
  }
  return getAiChatApiBase() ? 'rest' : 'dummy';
}

export function isAiChatDummyBackend() {
  return getAiChatBackendMode() === 'dummy';
}

/** When false (default), chat uses dummy replies instead of POST .../ai-chat/messages. */
export function useRestAiChatMessages() {
  return process.env.REACT_APP_AI_CHAT_USE_REST_MESSAGES === 'true';
}

/**
 * When true, chat uses POST /chatbot (SSE) with GET /chatbot/config/:moduleId settings.
 * Enabled if REACT_APP_AI_CHAT_API_BASE is set unless REACT_APP_AI_CHAT_USE_CHATBOT_SSE === 'false'.
 */
export function isChatbotSseEnabled() {
  const base = getAiChatApiBase();
  if (!base) {
    return false;
  }
  return process.env.REACT_APP_AI_CHAT_USE_CHATBOT_SSE !== 'false';
}

/** Full wss:// URL for chatbot WebSocket; empty if unset. */
export function getAiChatWebSocketUrl() {
  const u = process.env.REACT_APP_AI_CHAT_WS_BASE;
  return u && u.trim() !== '' ? u.trim().replace(/\/$/, '') : '';
}

/**
 * Live chatbot over WebSocket when explicitly enabled and URL is configured.
 * See file header for precedence over SSE.
 */
export function isChatbotWebSocketEnabled() {
  if (process.env.REACT_APP_AI_CHAT_USE_CHATBOT_WS !== 'true') {
    return false;
  }
  return Boolean(getAiChatWebSocketUrl());
}

/** When true, skip remote conversation APIs and use local demo assistant replies. */
export function isAiChatDemoResponsesEnabled() {
  return process.env.REACT_APP_AI_CHAT_DEMO_RESPONSES === 'true';
}

/** Header BETA chip is shown unless explicitly disabled. */
export function showAiChatBetaChip() {
  return process.env.REACT_APP_AI_CHAT_BETA_CHIP !== 'false';
}
